// src/components/settings/SalesAgentManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import settingsService from '../../services/settingsService';
import { organizationsService } from '../../services/organizationsService';
import { Logo } from '../../hooks/useLogo';
import Toast from '../common/Toast';

const SalesAgentManagement = () => {
    const navigate = useNavigate();
    const [salesAgents, setSalesAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [editingAgent, setEditingAgent] = useState(null);
    const [viewingAgent, setViewingAgent] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [organizations, setOrganizations] = useState([]);
    const [assignData, setAssignData] = useState({
        organization_id: '',
        sales_agent_id: '',
        sales_agent_start_date: '',
        sales_agent_end_date: '',
        sales_agent_commission_type: 'percentage',
        sales_agent_commission_value: ''
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
    const [formData, setFormData] = useState({
        name: '',
        cell_number: '',
        address: '',
        city: '',
        country: '',
        commission_type: 'percentage',
        commission_value: '',
        status: 1
    });
    const [formErrors, setFormErrors] = useState({});
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
    };

    const loadSalesAgents = useCallback(async () => {
        try {
            setLoading(true);
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                search: searchTerm,
                status: statusFilter
            };

            const response = await settingsService.getSalesAgents(params);
            setSalesAgents(response.sales_agents || []);
            if (response.pagination) {
                setPagination(prev => ({ ...prev, ...response.pagination }));
            }
        } catch (error) {
            showToast(error.message || 'Failed to load sales agents', 'error');
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.limit, searchTerm, statusFilter]);

    const loadOrganizations = useCallback(async () => {
        try {
            const result = await organizationsService.getOrganizationsList();
            setOrganizations(Array.isArray(result) ? result : []);
        } catch (error) {
            showToast(error.message || 'Failed to load organizations', 'error');
        }
    }, []);

    useEffect(() => {
        loadSalesAgents();
    }, [loadSalesAgents]);

    useEffect(() => {
        loadOrganizations();
    }, [loadOrganizations]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.name || formData.name.trim() === '') {
            errors.name = 'Name is required';
        }

        if (!formData.commission_value || formData.commission_value === '') {
            errors.commission_value = 'Commission value is required';
        } else {
            const value = parseFloat(formData.commission_value);
            if (isNaN(value) || value < 0) {
                errors.commission_value = 'Commission value must be a positive number';
            } else if (formData.commission_type === 'percentage' && value > 100) {
                errors.commission_value = 'Commission percentage cannot exceed 100%';
            }
        }

        setFormErrors(errors);
        return Object.keys(errors).length == 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            const submitData = {
                ...formData,
                commission_value: parseFloat(formData.commission_value)
            };

            if (editingAgent) {
                await settingsService.updateSalesAgent(editingAgent.id, submitData);
                showToast('Sales agent updated successfully', 'success');
            } else {
                await settingsService.createSalesAgent(submitData);
                showToast('Sales agent created successfully', 'success');
            }

            setShowModal(false);
            resetForm();
            loadSalesAgents();
        } catch (error) {
            showToast(error.message || 'Failed to save sales agent', 'error');
        }
    };

    const handleEdit = (agent) => {
        setEditingAgent(agent);
        setFormData({
            name: agent.name || '',
            cell_number: agent.cell_number || '',
            address: agent.address || '',
            city: agent.city || '',
            country: agent.country || '',
            commission_type: agent.commission_type || 'percentage',
            commission_value: agent.commission_value || '',
            status: agent.status
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (id == 1) {
            showToast('Cannot delete the default Core Admin sales agent', 'error');
            return;
        }

        if (!window.confirm('Are you sure you want to delete this sales agent? This action cannot be undone.')) {
            return;
        }

        try {
            await settingsService.deleteSalesAgent(id);
            showToast('Sales agent deleted successfully', 'success');
            loadSalesAgents();
        } catch (error) {
            showToast(error.message || 'Failed to delete sales agent', 'error');
        }
    };

    const handleView = async (agent) => {
        try {
            setDetailsLoading(true);
            setShowDetailsModal(true);
            const details = await settingsService.getSalesAgentDetails(agent.id);
            setViewingAgent(details);
        } catch (error) {
            setShowDetailsModal(false);
            showToast(error.message || 'Failed to load sales agent details', 'error');
        } finally {
            setDetailsLoading(false);
        }
    };

    const handleAssignOpen = (agent) => {
        setAssignData({
            organization_id: '',
            sales_agent_id: String(agent.id),
            sales_agent_start_date: '',
            sales_agent_end_date: '',
            sales_agent_commission_type: agent.commission_type || 'percentage',
            sales_agent_commission_value: agent.commission_value || ''
        });
        setShowAssignModal(true);
    };

    const handleAssignSubmit = async (e) => {
        e.preventDefault();
        if (!assignData.organization_id || !assignData.sales_agent_id) {
            showToast('Organization and sales agent are required', 'error');
            return;
        }

        try {
            await settingsService.assignSalesAgentToOrganization({
                organization_id: Number(assignData.organization_id),
                sales_agent_id: Number(assignData.sales_agent_id),
                sales_agent_start_date: assignData.sales_agent_start_date || undefined,
                sales_agent_end_date: assignData.sales_agent_end_date || undefined,
                sales_agent_commission_type: assignData.sales_agent_commission_type,
                sales_agent_commission_value: assignData.sales_agent_commission_value !== ''
                    ? Number(assignData.sales_agent_commission_value)
                    : undefined
            });
            showToast('Sales agent assigned to organization successfully', 'success');
            setShowAssignModal(false);
            loadSalesAgents();
        } catch (error) {
            showToast(error.message || 'Failed to assign sales agent', 'error');
        }
    };

    const handlePrintDetailsReport = () => {
        if (!viewingAgent) return;

        const ag = viewingAgent.sales_agent ?? {};
        const sm = viewingAgent.summary ?? {};
        const orgs = Array.isArray(viewingAgent.organizations) ? viewingAgent.organizations : [];
        const fmt0 = (n) => Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
        const fmt2 = (n) => Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
        const commDisplay = (type, val) => type === 'percentage' ? `${val}%` : `${fmt0(val)} (fixed)`;
        const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
        const rawLogoUrl = localStorage.getItem('app_logo') || '';
        const apiBase = (process.env.REACT_APP_API_URL || window.location.origin || '').replace(/\/+$/, '');
        const logoUrl = (() => {
            const value = String(rawLogoUrl || '').trim();
            if (!value) return '';
            if (/^https?:\/\//i.test(value) || value.startsWith('data:image/')) return value;
            if (value.startsWith('/')) return `${apiBase}${value}`;
            return `${apiBase}/${value}`;
        })();

        const rowsPerPage = 18;
        const pages = [];
        for (let i = 0; i < orgs.length; i += rowsPerPage) {
            pages.push(orgs.slice(i, i + rowsPerPage));
        }
        if (pages.length === 0) pages.push([]);

        const win = window.open('', '_blank');
        if (!win) {
            showToast('Please allow popups to print this report', 'error');
            return;
        }

        const headerHtml = `
            <div class="ar-header">
                <div class="ar-logo-section">
                    ${logoUrl
                        ? `<img class="ar-logo-img" src="${esc(logoUrl)}" alt="Logo" onerror="this.style.display='none';this.nextElementSibling.style.display='block';" /><div class="logo-fallback" style="display:none;">eHAAZRI</div>`
                        : `<div class="logo-fallback">eHAAZRI</div>`
                    }
                    <div class="ar-company-info">
                        <span>Attendance Management System</span>
                        <span>support@ehaazri.com</span>
                    </div>
                </div>
                <div class="ar-report-meta">
                    <div class="ar-report-title">Sales Agent Report</div>
                    <div class="ar-report-date">Generated: ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</div>
                    <div class="ar-report-date">Period: All-time</div>
                </div>
            </div>
            <hr class="ar-divider" />
            <div class="ar-agent-block">
                <div class="ar-agent-name-row">
                    <span class="ar-agent-name">${esc(ag.name || '-')}</span>
                    <span class="ar-badge ${Number(ag.status) === 1 ? 'active' : 'inactive'}">${Number(ag.status) === 1 ? 'Active' : 'Inactive'}</span>
                </div>
                <div class="ar-agent-details">
                    ${ag.cell_number ? `<span>Phone: ${esc(ag.cell_number)}</span>` : ''}
                    ${(ag.city || ag.country) ? `<span>Location: ${esc([ag.city, ag.country].filter(Boolean).join(', '))}</span>` : ''}
                    <span>Default commission: <strong>${esc(commDisplay(ag.commission_type, ag.commission_value))}</strong></span>
                </div>
            </div>
            <div class="ar-kpi-grid">
                <div class="ar-kpi-item"><div class="ar-kpi-label">Orgs Assigned</div><div class="ar-kpi-value">${sm.organizations_assigned ?? 0}</div><div class="ar-kpi-sub">${sm.active_organizations ?? 0} active</div></div>
                <div class="ar-kpi-item"><div class="ar-kpi-label">Payments Completed</div><div class="ar-kpi-value">${sm.completed_payments ?? 0}</div><div class="ar-kpi-sub">of ${sm.total_payments ?? 0} total</div></div>
                <div class="ar-kpi-item pkr"><div class="ar-kpi-label">Collected (PKR)</div><div class="ar-kpi-value">PKR ${fmt0(sm.total_collected_pkr)}</div></div>
                <div class="ar-kpi-item usd"><div class="ar-kpi-label">Collected (USD)</div><div class="ar-kpi-value">USD ${fmt2(sm.total_collected_usd)}</div></div>
                <div class="ar-kpi-item comm-pkr"><div class="ar-kpi-label">Commission (PKR)</div><div class="ar-kpi-value">PKR ${fmt0(sm.total_commission_pkr)}</div></div>
                <div class="ar-kpi-item comm-usd"><div class="ar-kpi-label">Commission (USD)</div><div class="ar-kpi-value">USD ${fmt2(sm.total_commission_usd)}</div></div>
            </div>
            <div class="ar-payable-banner">
                <div><strong>Total Payable to Agent</strong> ${
                    Number(sm.total_commission_pkr ?? 0) > 0 || Number(sm.total_commission_usd ?? 0) > 0
                        ? `${Number(sm.total_commission_pkr ?? 0) > 0 ? `PKR ${fmt0(sm.total_commission_pkr)}` : ''}${Number(sm.total_commission_pkr ?? 0) > 0 && Number(sm.total_commission_usd ?? 0) > 0 ? ' + ' : ''}${Number(sm.total_commission_usd ?? 0) > 0 ? `USD ${fmt2(sm.total_commission_usd)}` : ''}`
                        : '- No commission earned yet'
                }</div>
            </div>
        `;

        const pageHtml = pages.map((chunk, pageIndex) => {
            const rows = chunk.map((item, idx) => {
                const org = item.organization ?? {};
                const m = item.metrics ?? {};
                const activeSub = item.active_subscription;
                const staffCount = activeSub?.staff_count ?? '-';
                const subEnd = activeSub?.end_date ? fmtDate(activeSub.end_date) : (activeSub ? 'No Expiry' : '-');
                const rate = commDisplay(item.commission_type, item.commission_value);
                const rowNo = pageIndex * rowsPerPage + idx + 1;
                return `
                    <tr>
                        <td>${rowNo}</td>
                        <td><strong>${esc(org.name)}</strong><br/><small>${esc(org.contact_email || '')}</small></td>
                        <td>${Number(org.status) === 2 ? 'Active' : 'Inactive'}</td>
                        <td class="ar-td-center">${esc(staffCount)}</td>
                        <td>${esc(subEnd)}</td>
                        <td class="ar-td-right">${m.completed_payments ?? 0}</td>
                        <td class="ar-td-right">PKR ${fmt0(m.total_collected_pkr)}</td>
                        <td class="ar-td-right">USD ${fmt2(m.total_collected_usd)}</td>
                        <td class="ar-td-right">${esc(rate)}</td>
                        <td class="ar-td-right">PKR ${fmt0(m.commission_pkr)}</td>
                        <td class="ar-td-right">USD ${fmt2(m.commission_usd)}</td>
                    </tr>
                `;
            }).join('');

            const isLast = pageIndex === pages.length - 1;
            return `
                <div class="print-page">
                    ${pageIndex === 0 ? headerHtml : ''}
                    <h3 class="ar-section-heading">Organisation Breakdown</h3>
                    <table class="ar-table">
                        <thead>
                            <tr>
                                <th>#</th><th>Organisation</th><th>Status</th><th>Staff</th><th>Sub. Ends</th>
                                <th class="ar-th-right">Payments</th><th class="ar-th-right">Collected (PKR)</th><th class="ar-th-right">Collected (USD)</th>
                                <th class="ar-th-right">Comm. Rate</th><th class="ar-th-right">Comm. (PKR)</th><th class="ar-th-right">Comm. (USD)</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${rows || '<tr><td colspan="11" style="text-align:center; color:#718096; padding:16px;">No organisations assigned</td></tr>'}
                        </tbody>
                    </table>
                    ${isLast ? `
                        <div class="ar-grand-total-rt">
                            <div class="gt-title">Grand Total</div>
                            <div class="gt-values">
                                <span><strong>Payments:</strong> ${sm.completed_payments ?? 0}</span>
                                <span><strong>Collected PKR:</strong> PKR ${fmt0(sm.total_collected_pkr)}</span>
                                <span><strong>Collected USD:</strong> USD ${fmt2(sm.total_collected_usd)}</span>
                                <span><strong>Commission PKR:</strong> PKR ${fmt0(sm.total_commission_pkr)}</span>
                                <span><strong>Commission USD:</strong> USD ${fmt2(sm.total_commission_usd)}</span>
                            </div>
                        </div>
                    ` : ''}
                </div>
            `;
        }).join('');

        win.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Sales Agent Report - ${esc(ag.name || 'Agent')}</title>
                <style>
                    @page { size: A4 landscape; margin: 10mm; }
                    body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #1a202c; }
                    .print-page { page-break-after: always; }
                    .print-page:last-child { page-break-after: auto; }
                    .ar-header { display:flex; justify-content:space-between; align-items:flex-start; gap:1rem; margin-bottom:1rem; }
                    .ar-logo-section { display:flex; align-items:center; gap:.75rem; }
                    .ar-logo-img { height: 46px; max-width: 210px; object-fit: contain; }
                    .logo-fallback { font-weight:700; font-size:18px; }
                    .ar-company-info { display:flex; flex-direction:column; font-size:10px; color:#555; }
                    .ar-report-meta { text-align:right; }
                    .ar-report-title { font-size:16px; font-weight:700; }
                    .ar-report-date { font-size:10px; color:#718096; margin-top:2px; }
                    .ar-divider { border:0; border-top:1px solid #9a9a9a; margin:.9rem 0; }
                    .ar-agent-name-row { display:flex; align-items:center; gap:.5rem; margin-bottom:.4rem; }
                    .ar-agent-name { font-size:15px; font-weight:700; }
                    .ar-badge { padding:2px 10px; border-radius:999px; font-size:10px; font-weight:700; }
                    .ar-badge.active { background:#c6f6d5; color:#22543d; } .ar-badge.inactive { background:#fed7d7; color:#742a2a; }
                    .ar-agent-details { display:flex; flex-wrap:wrap; gap:1rem; font-size:11px; color:#4a5568; }
                    .ar-kpi-grid { display:grid; grid-template-columns:repeat(6,1fr); gap:.6rem; margin-bottom:1rem; }
                    .ar-kpi-item { padding:.6rem; border-radius:8px; background:#f7fafc; border:1px solid #e2e8f0; text-align:center; }
                    .ar-kpi-label { font-size:10px; text-transform:uppercase; color:#718096; font-weight:600; margin-bottom:2px; }
                    .ar-kpi-value { font-size:14px; font-weight:700; }
                    .ar-kpi-sub { font-size:10px; color:#a0aec0; }
                    .ar-payable-banner { padding:.7rem 1rem; border-radius:8px; background:rgba(22,163,74,.08); border-left:4px solid #16a34a; margin-bottom:1rem; font-size:12px; }
                    .ar-section-heading { font-size:12px; font-weight:700; margin:0 0 .5rem; }
                    .ar-table { width:100%; border-collapse:collapse; table-layout:fixed; font-size:11px; }
                    .ar-table thead { display:table-header-group; }
                    .ar-table tr { page-break-inside:avoid; break-inside:avoid; }
                    .ar-table th { background:#9a9a9a; color:#fff; padding:6px 7px; text-align:left; }
                    .ar-table td { border-bottom:1px solid #e2e8f0; padding:6px 7px; vertical-align:middle; }
                    .ar-th-right, .ar-td-right { text-align:right !important; }
                    .ar-td-center { text-align:center !important; }
                    .ar-grand-total-rt { margin-top:8px; border:1px solid #e2e8f0; border-radius:8px; padding:8px 10px; background:#f8fafc; page-break-inside:avoid; }
                    .ar-grand-total-rt .gt-title { font-size:11px; font-weight:700; margin-bottom:4px; text-transform:uppercase; }
                    .ar-grand-total-rt .gt-values { display:flex; gap:16px; flex-wrap:wrap; font-size:12px; }
                </style>
            </head>
            <body>${pageHtml}</body>
            </html>
        `);
        win.document.close();
        win.focus();
        win.onload = () => {
            const logoImg = win.document.querySelector('.ar-logo-img');
            const safePrint = () => {
                setTimeout(() => {
                    win.print();
                    win.close();
                }, 120);
            };
            if (!logoImg) return safePrint();
            if (logoImg.complete && logoImg.naturalWidth > 0) return safePrint();
            let done = false;
            const finalize = () => {
                if (done) return;
                done = true;
                safePrint();
            };
            logoImg.onload = finalize;
            logoImg.onerror = finalize;
            setTimeout(finalize, 1200);
        };
    };

    const resetForm = () => {
        setFormData({
            name: '',
            cell_number: '',
            address: '',
            city: '',
            country: '',
            commission_type: 'percentage',
            commission_value: '',
            status: 1
        });
        setFormErrors({});
        setEditingAgent(null);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        resetForm();
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleStatusFilter = (e) => {
        setStatusFilter(e.target.value);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    return (
        <div className="sales-agent-management">
            <div className="page-header">
                <h2>Sales Agent Management</h2>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button onClick={() => navigate('/reports', { state: { activeReport: 'sales-agents' } })} className="btn-secondary">
                        <i className="fas fa-chart-bar"></i> View Detailed Report
                    </button>
                    <button onClick={() => setShowModal(true)} className="btn-primary">
                        <i className="fas fa-plus"></i> Add Sales Agent
                    </button>
                </div>
            </div>

            <div className="filters">
                <div className="search-box">
                    <i className="fas fa-search"></i>
                    <input
                        type="text"
                        placeholder="Search sales agents..."
                        value={searchTerm}
                        onChange={handleSearch}
                    />
                </div>

                <select value={statusFilter} onChange={handleStatusFilter} className="filter-select">
                    <option value="">All Status</option>
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                </select>
            </div>

            {loading ? (
                <div className="loading">Loading sales agents...</div>
            ) : (
                <>
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Cell Number</th>
                                    <th>City</th>
                                    <th>Country</th>
                                    <th>Commission</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {salesAgents.length == 0 ? (
                                    <tr>
                                        <td colSpan="7" style={{ textAlign: 'center', padding: '2rem' }}>
                                            No sales agents found
                                        </td>
                                    </tr>
                                ) : (
                                    salesAgents.map((agent) => (
                                        <tr key={agent.id}>
                                            <td>
                                                <strong>{agent.name}</strong>
                                                {agent.id == 1 && (
                                                    <span style={{ marginLeft: '8px', fontSize: '11px', background: '#667eea', color: 'white', padding: '2px 6px', borderRadius: '3px' }}>
                                                        DEFAULT
                                                    </span>
                                                )}
                                            </td>
                                            <td>{agent.cell_number || '-'}</td>
                                            <td>{agent.city || '-'}</td>
                                            <td>{agent.country || '-'}</td>
                                            <td>
                                                {agent.commission_type === 'percentage'
                                                    ? `${agent.commission_value}%`
                                                    : `$${agent.commission_value}`}
                                            </td>
                                            <td>
                                                <span className={`status-badge ${agent.status == 1 ? 'active' : 'inactive'}`}>
                                                    {agent.status == 1 ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', gap: '8px' }}>
                                                    <button
                                                        onClick={() => handleView(agent)}
                                                        style={{
                                                            padding: '6px 12px',
                                                            backgroundColor: '#2563eb',
                                                            color: '#fff',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontSize: '13px',
                                                            fontWeight: 600
                                                        }}
                                                        title="View organizations, subscriptions, and payment details"
                                                    >
                                                        <i className="fas fa-eye"></i> View More
                                                    </button>
                                                    <button
                                                        onClick={() => handleAssignOpen(agent)}
                                                        style={{
                                                            padding: '6px 12px',
                                                            backgroundColor: '#28a745',
                                                            color: '#fff',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontSize: '13px'
                                                        }}
                                                    >
                                                        <i className="fas fa-link"></i> Assign
                                                    </button>
                                                    <button
                                                        onClick={() => handleEdit(agent)}
                                                        style={{
                                                            padding: '6px 12px',
                                                            backgroundColor: '#667eea',
                                                            color: '#fff',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontSize: '13px'
                                                        }}
                                                    >
                                                        <i className="fas fa-edit"></i> Edit
                                                    </button>
                                                    {agent.id != 1 && (
                                                        <button
                                                            onClick={() => handleDelete(agent.id)}
                                                            style={{
                                                                padding: '6px 12px',
                                                                backgroundColor: '#dc3545',
                                                                color: '#fff',
                                                                border: 'none',
                                                                borderRadius: '4px',
                                                                cursor: 'pointer',
                                                                fontSize: '13px'
                                                            }}
                                                        >
                                                            <i className="fas fa-trash"></i> Delete
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {pagination.pages > 1 && (
                        <div className="pagination">
                            <button
                                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                disabled={pagination.page === 1}
                            >
                                Previous
                            </button>
                            <span>Page {pagination.page} of {pagination.pages}</span>
                            <button
                                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                disabled={pagination.page === pagination.pages}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}

            {showModal && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{editingAgent ? 'Edit Sales Agent' : 'Add New Sales Agent'}</h3>
                            <button className="close-btn" onClick={handleCloseModal}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>
                                        Name <span className="required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        disabled={editingAgent && editingAgent.id === 1}
                                    />
                                    {formErrors.name && <span className="error-text">{formErrors.name}</span>}
                                </div>

                                <div className="form-group">
                                    <label>Cell Number</label>
                                    <input
                                        type="text"
                                        name="cell_number"
                                        value={formData.cell_number}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div className="form-group full-width">
                                    <label>Address</label>
                                    <textarea
                                        name="address"
                                        value={formData.address}
                                        onChange={handleInputChange}
                                        rows="3"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>City</label>
                                    <input
                                        type="text"
                                        name="city"
                                        value={formData.city}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Country</label>
                                    <input
                                        type="text"
                                        name="country"
                                        value={formData.country}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>
                                        Commission Type <span className="required">*</span>
                                    </label>
                                    <select
                                        name="commission_type"
                                        value={formData.commission_type}
                                        onChange={handleInputChange}
                                    >
                                        <option value="percentage">Percentage</option>
                                        <option value="fixed">Fixed Amount</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>
                                        Commission Value <span className="required">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        name="commission_value"
                                        value={formData.commission_value}
                                        onChange={handleInputChange}
                                        step="0.01"
                                        min="0"
                                        max={formData.commission_type === 'percentage' ? '100' : undefined}
                                    />
                                    {formErrors.commission_value && <span className="error-text">{formErrors.commission_value}</span>}
                                </div>

                                <div className="form-group">
                                    <label>Status</label>
                                    <select
                                        name="status"
                                        value={formData.status}
                                        onChange={handleInputChange}
                                    >
                                        <option value={1}>Active</option>
                                        <option value={0}>Inactive</option>
                                    </select>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="button" onClick={handleCloseModal} className="btn-secondary">
                                    Cancel
                                </button>
                                <button type="submit" className="btn-primary">
                                    {editingAgent ? 'Update Sales Agent' : 'Create Sales Agent'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showDetailsModal && (
                <div className="modal-overlay ar-modal-overlay" onClick={() => setShowDetailsModal(false)}>
                    <div className="modal-content ar-modal-content" onClick={(e) => e.stopPropagation()}>
                        {/* Toolbar (hidden on print) */}
                        <div className="modal-header ar-toolbar no-print">
                            <h3><i className="fas fa-user-tie" style={{ marginRight: '8px' }}></i>Sales Agent Report</h3>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <button onClick={handlePrintDetailsReport} className="btn-primary" style={{ padding: '6px 14px', fontSize: '13px' }}>
                                    <i className="fas fa-print"></i> Print / PDF
                                </button>
                                <button className="close-btn" onClick={() => setShowDetailsModal(false)}>
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                        </div>

                        {/* Printable report body */}
                        <div className="ar-report-wrap">
                            {detailsLoading ? (
                                <div className="loading">Loading report…</div>
                            ) : !viewingAgent ? (
                                <div className="loading">No details found.</div>
                            ) : (() => {
                                const ag = viewingAgent.sales_agent ?? {};
                                const sm = viewingAgent.summary ?? {};
                                const orgs = viewingAgent.organizations ?? [];
                                const fmt0 = (n) => Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
                                const fmt2 = (n) => Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : null;
                                const commDisplay = (type, val) => type === 'percentage' ? `${val}%` : `${fmt0(val)} (fixed)`;

                                return (<>
                                    {/* ── Report Header ── */}
                                    <div className="ar-header">
                                        <div className="ar-logo-section">
                                            <Logo size="large" className="ar-logo" />
                                            <div className="ar-company-info">
                                                <span>Attendance Management System</span>
                                                <span>support@ehaazri.com</span>
                                            </div>
                                        </div>
                                        <div className="ar-report-meta">
                                            <div className="ar-report-title">Sales Agent Report</div>
                                            <div className="ar-report-date">Generated: {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</div>
                                            <div className="ar-report-date">Period: All-time</div>
                                        </div>
                                    </div>

                                    <hr className="ar-divider" />

                                    {/* ── Agent Info ── */}
                                    <div className="ar-agent-block">
                                        <div className="ar-agent-name-row">
                                            <i className="fas fa-user-tie ar-agent-icon"></i>
                                            <span className="ar-agent-name">{ag.name || '—'}</span>
                                            <span className={`ar-badge ${ag.status == 1 ? 'active' : 'inactive'}`}>
                                                {ag.status == 1 ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                        <div className="ar-agent-details">
                                            {ag.cell_number && <span><i className="fas fa-phone"></i> {ag.cell_number}</span>}
                                            {(ag.city || ag.country) && <span><i className="fas fa-map-marker-alt"></i> {[ag.city, ag.country].filter(Boolean).join(', ')}</span>}
                                            <span><i className="fas fa-percent"></i> Default commission: <strong>{commDisplay(ag.commission_type, ag.commission_value)}</strong></span>
                                        </div>
                                    </div>

                                    {/* ── KPI Strip ── */}
                                    <div className="ar-kpi-grid">
                                        <div className="ar-kpi-item">
                                            <div className="ar-kpi-label">Orgs Assigned</div>
                                            <div className="ar-kpi-value">{sm.organizations_assigned ?? 0}</div>
                                            <div className="ar-kpi-sub">{sm.active_organizations ?? 0} active</div>
                                        </div>
                                        <div className="ar-kpi-item">
                                            <div className="ar-kpi-label">Payments Completed</div>
                                            <div className="ar-kpi-value">{sm.completed_payments ?? 0}</div>
                                            <div className="ar-kpi-sub">of {sm.total_payments ?? 0} total</div>
                                        </div>
                                        <div className="ar-kpi-item pkr">
                                            <div className="ar-kpi-label">Collected (PKR)</div>
                                            <div className="ar-kpi-value">₨ {fmt0(sm.total_collected_pkr)}</div>
                                        </div>
                                        <div className="ar-kpi-item usd">
                                            <div className="ar-kpi-label">Collected (USD)</div>
                                            <div className="ar-kpi-value">$ {fmt2(sm.total_collected_usd)}</div>
                                        </div>
                                        <div className="ar-kpi-item comm-pkr">
                                            <div className="ar-kpi-label">Commission (PKR)</div>
                                            <div className="ar-kpi-value">₨ {fmt0(sm.total_commission_pkr)}</div>
                                        </div>
                                        <div className="ar-kpi-item comm-usd">
                                            <div className="ar-kpi-label">Commission (USD)</div>
                                            <div className="ar-kpi-value">$ {fmt2(sm.total_commission_usd)}</div>
                                        </div>
                                    </div>

                                    {/* ── Payable banner ── */}
                                    <div className="ar-payable-banner">
                                        <i className="fas fa-file-invoice-dollar"></i>
                                        <div>
                                            <strong>Total Payable to Agent</strong>
                                            <span>
                                                {Number(sm.total_commission_pkr ?? 0) > 0 && `₨ ${fmt0(sm.total_commission_pkr)} (PKR)`}
                                                {Number(sm.total_commission_pkr ?? 0) > 0 && Number(sm.total_commission_usd ?? 0) > 0 && '  +  '}
                                                {Number(sm.total_commission_usd ?? 0) > 0 && `$ ${fmt2(sm.total_commission_usd)} (USD)`}
                                                {Number(sm.total_commission_pkr ?? 0) === 0 && Number(sm.total_commission_usd ?? 0) === 0 && '— No commission earned yet'}
                                            </span>
                                        </div>
                                    </div>

                                    {/* ── Organisation Breakdown Table ── */}
                                    <h3 className="ar-section-heading">Organisation Breakdown</h3>
                                    <div className="ar-table-wrap">
                                        <table className="ar-table">
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Organisation</th>
                                                    <th>Status</th>
                                                    <th>Staff</th>
                                                    <th>Sub. Ends</th>
                                                    <th className="ar-th-right">Payments</th>
                                                    <th className="ar-th-right">Collected (PKR)</th>
                                                    <th className="ar-th-right">Collected (USD)</th>
                                                    <th className="ar-th-right">Comm. Rate</th>
                                                    <th className="ar-th-right">Comm. (PKR)</th>
                                                    <th className="ar-th-right">Comm. (USD)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {orgs.length === 0 ? (
                                                    <tr><td colSpan="11" style={{ textAlign: 'center', color: '#718096', padding: '2rem' }}>No organisations assigned</td></tr>
                                                ) : orgs.map((item, idx) => {
                                                    const org = item.organization ?? {};
                                                    const m = item.metrics ?? {};
                                                    const activeSub = item.active_subscription;
                                                    const staffCount = activeSub?.staff_count ?? '—';
                                                    const subEnd = activeSub?.end_date ? fmtDate(activeSub.end_date) : (activeSub ? '∞ No Expiry' : '—');
                                                    const rate = commDisplay(item.commission_type, item.commission_value);
                                                    return (
                                                        <tr key={org.id ?? idx}>
                                                            <td>{idx + 1}</td>
                                                            <td>
                                                                <strong style={{ display: 'block' }}>{org.name}</strong>
                                                                {org.contact_email && <small style={{ color: '#718096' }}>{org.contact_email}</small>}
                                                            </td>
                                                            <td>
                                                                <span className={`ar-badge ${org.status == 2 ? 'active' : 'inactive'}`}>
                                                                    {org.status == 2 ? 'Active' : 'Inactive'}
                                                                </span>
                                                            </td>
                                                            <td className="ar-td-center">{staffCount}</td>
                                                            <td style={{ whiteSpace: 'nowrap' }}>{subEnd}</td>
                                                            <td className="ar-td-right">{m.completed_payments ?? 0}</td>
                                                            <td className="ar-td-right">₨ {fmt0(m.total_collected_pkr)}</td>
                                                            <td className="ar-td-right">$ {fmt2(m.total_collected_usd)}</td>
                                                            <td className="ar-td-right">{rate}</td>
                                                            <td className="ar-td-right ar-comm">₨ {fmt0(m.commission_pkr)}</td>
                                                            <td className="ar-td-right ar-comm">$ {fmt2(m.commission_usd)}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                            <tfoot>
                                                <tr className="ar-total-row">
                                                    <td colSpan="5" className="ar-td-label">TOTAL</td>
                                                    <td className="ar-td-right">{sm.completed_payments ?? 0}</td>
                                                    <td className="ar-td-right">₨ {fmt0(sm.total_collected_pkr)}</td>
                                                    <td className="ar-td-right">$ {fmt2(sm.total_collected_usd)}</td>
                                                    <td></td>
                                                    <td className="ar-td-right ar-comm">₨ {fmt0(sm.total_commission_pkr)}</td>
                                                    <td className="ar-td-right ar-comm">$ {fmt2(sm.total_commission_usd)}</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>

                                    {/* ── Notes ── */}
                                    <div className="ar-notes">
                                        <p>This report is auto-generated and covers all-time payments for assigned organisations.</p>
                                        <p>Commission is calculated per completed payment using the organisation-specific rate (if set) or the agent's default rate.</p>
                                        <p>Staff count and subscription end date reflect the current active subscription for each organisation.</p>
                                    </div>
                                </>);
                            })()}
                        </div>
                    </div>
                </div>
            )}

            {showAssignModal && (
                <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Assign Sales Agent</h3>
                            <button className="close-btn" onClick={() => setShowAssignModal(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <form onSubmit={handleAssignSubmit}>
                            <div className="form-grid">
                                <div className="form-group full-width">
                                    <label>Organization <span className="required">*</span></label>
                                    <select
                                        value={assignData.organization_id}
                                        onChange={(e) => setAssignData((prev) => ({ ...prev, organization_id: e.target.value }))}
                                    >
                                        <option value="">Select organization</option>
                                        {organizations.map((org) => (
                                            <option key={org.id} value={org.id}>{org.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Start Date</label>
                                    <input
                                        type="date"
                                        value={assignData.sales_agent_start_date}
                                        onChange={(e) => setAssignData((prev) => ({ ...prev, sales_agent_start_date: e.target.value }))}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>End Date</label>
                                    <input
                                        type="date"
                                        value={assignData.sales_agent_end_date}
                                        onChange={(e) => setAssignData((prev) => ({ ...prev, sales_agent_end_date: e.target.value }))}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Commission Type</label>
                                    <select
                                        value={assignData.sales_agent_commission_type}
                                        onChange={(e) => setAssignData((prev) => ({ ...prev, sales_agent_commission_type: e.target.value }))}
                                    >
                                        <option value="percentage">Percentage</option>
                                        <option value="fixed">Fixed</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Commission Value</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={assignData.sales_agent_commission_value}
                                        onChange={(e) => setAssignData((prev) => ({ ...prev, sales_agent_commission_value: e.target.value }))}
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" onClick={() => setShowAssignModal(false)} className="btn-secondary">
                                    Cancel
                                </button>
                                <button type="submit" className="btn-primary">
                                    Assign Agent
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style jsx>{`
                .sales-agent-management {
                    padding: 1.5rem;
                }

                .page-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 2rem;
                }

                .page-header h2 {
                    margin: 0;
                    font-size: 1.75rem;
                    color: #2d3748;
                }

                .filters {
                    display: flex;
                    gap: 1rem;
                    margin-bottom: 1.5rem;
                    flex-wrap: wrap;
                }

                .search-box {
                    position: relative;
                    flex: 1;
                    min-width: 250px;
                }

                .search-box i {
                    position: absolute;
                    left: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #a0aec0;
                }

                .search-box input {
                    width: 100%;
                    padding: 10px 10px 10px 40px;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    font-size: 14px;
                }

                .filter-select {
                    padding: 10px 16px;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    font-size: 14px;
                    min-width: 150px;
                }

                .btn-primary {
                    padding: 10px 20px;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    border: none;
                    border-radius: 8px;
                    cursor: pointer;
                    font-size: 14px;
                    font-weight: 600;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    transition: transform 0.2s;
                }

                .btn-primary:hover {
                    transform: translateY(-2px);
                }

                .btn-secondary {
                    padding: 10px 20px;
                    background: #e2e8f0;
                    color: #2d3748;
                    border: none;
                    border-radius: 8px;
                    cursor: pointer;
                    font-size: 14px;
                    font-weight: 600;
                }

                .loading {
                    text-align: center;
                    padding: 3rem;
                    color: #718096;
                }

                .table-container {
                    background: white;
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
                }

                .data-table {
                    width: 100%;
                    border-collapse: collapse;
                }

                .data-table thead {
                    background: #f7fafc;
                }

                .data-table th {
                    padding: 1rem;
                    text-align: left;
                    font-weight: 600;
                    color: #2d3748;
                    font-size: 14px;
                    border-bottom: 2px solid #e2e8f0;
                }

                .data-table td {
                    padding: 1rem;
                    border-bottom: 1px solid #f7fafc;
                    font-size: 14px;
                    color: #4a5568;
                }

                .data-table tbody tr:hover {
                    background: #f7fafc;
                }

                .status-badge {
                    padding: 4px 12px;
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 600;
                }

                .status-badge.active {
                    background: #c6f6d5;
                    color: #22543d;
                }

                .status-badge.inactive {
                    background: #fed7d7;
                    color: #742a2a;
                }

                .pagination {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 1rem;
                    margin-top: 1.5rem;
                }

                .pagination button {
                    padding: 8px 16px;
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    cursor: pointer;
                    font-size: 14px;
                }

                .pagination button:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: 1rem;
                }

                .modal-content {
                    background: white;
                    border-radius: 16px;
                    width: 100%;
                    max-width: 600px;
                    max-height: 90vh;
                    overflow-y: auto;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1.5rem;
                    border-bottom: 1px solid #e2e8f0;
                }

                .modal-header h3 {
                    margin: 0;
                    font-size: 1.25rem;
                    color: #2d3748;
                }

                .close-btn {
                    background: none;
                    border: none;
                    font-size: 1.5rem;
                    color: #718096;
                    cursor: pointer;
                    padding: 0;
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 6px;
                }

                .close-btn:hover {
                    background: #f7fafc;
                    color: #2d3748;
                }

                form {
                    padding: 1.5rem;
                }

                .form-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 1.5rem;
                }

                .form-group {
                    display: flex;
                    flex-direction: column;
                }

                .form-group.full-width {
                    grid-column: 1 / -1;
                }

                .form-group label {
                    margin-bottom: 0.5rem;
                    font-weight: 500;
                    font-size: 14px;
                    color: #2d3748;
                }

                .required {
                    color: #e53e3e;
                }

                .form-group input,
                .form-group select,
                .form-group textarea {
                    padding: 10px 12px;
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    font-size: 14px;
                }

                .form-group input:focus,
                .form-group select:focus,
                .form-group textarea:focus {
                    outline: none;
                    border-color: #667eea;
                    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
                }

                .error-text {
                    color: #e53e3e;
                    font-size: 12px;
                    margin-top: 4px;
                }

                .modal-footer {
                    display: flex;
                    justify-content: flex-end;
                    gap: 1rem;
                    padding-top: 1.5rem;
                    border-top: 1px solid #e2e8f0;
                    margin-top: 1.5rem;
                }

                @media (max-width: 768px) {
                    .form-grid { grid-template-columns: 1fr; }
                    .page-header { flex-direction: column; align-items: flex-start; gap: 1rem; }
                    .table-container { overflow-x: auto; }
                }

                /* ── Agent Report Modal ── */
                .ar-modal-overlay { align-items: flex-start; padding: 1rem; overflow-y: auto; }
                .ar-modal-content {
                    max-width: 1100px;
                    max-height: none;
                    overflow-y: visible;
                }

                /* ── Report wrap (printable area) ── */
                .ar-report-wrap {
                    padding: 1.5rem 2rem 2rem;
                    font-family: Arial, Helvetica, sans-serif;
                    font-size: 12pt;
                    color: #1a202c;
                    background: #fff;
                }

                /* Header */
                .ar-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    gap: 1rem;
                    margin-bottom: 1rem;
                }
                .ar-logo-section { display: flex; align-items: center; gap: 0.75rem; }
                .ar-logo { max-height: 52px; }
                .ar-company-info { display: flex; flex-direction: column; font-size: 9pt; color: #555; gap: 2px; }
                .ar-company-info span:first-child { font-weight: 600; font-size: 10pt; color: #1a202c; }
                .ar-report-meta { text-align: right; }
                .ar-report-title { font-size: 15pt; font-weight: 700; color: #1a202c; }
                .ar-report-date { font-size: 9pt; color: #718096; margin-top: 2px; }
                .ar-divider { border: 0; border-top: 1px solid #9a9a9a; margin: 0.9rem 0; }

                /* Agent info */
                .ar-agent-block { margin-bottom: 1rem; }
                .ar-agent-name-row { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.4rem; }
                .ar-agent-icon { font-size: 1.1rem; color: #667eea; }
                .ar-agent-name { font-size: 14pt; font-weight: 700; color: #1a202c; }
                .ar-badge {
                    display: inline-block; padding: 2px 10px; border-radius: 999px;
                    font-size: 8pt; font-weight: 700; letter-spacing: 0.03em;
                }
                .ar-badge.active { background: #c6f6d5; color: #22543d; }
                .ar-badge.inactive { background: #fed7d7; color: #742a2a; }
                .ar-agent-details { display: flex; flex-wrap: wrap; gap: 1rem; font-size: 10pt; color: #4a5568; }
                .ar-agent-details span { display: flex; align-items: center; gap: 0.35rem; }
                .ar-agent-details i { color: #a0aec0; font-size: 0.85em; }

                /* KPI grid */
                .ar-kpi-grid {
                    display: grid;
                    grid-template-columns: repeat(6, 1fr);
                    gap: 0.6rem;
                    margin-bottom: 1rem;
                }
                .ar-kpi-item {
                    padding: 0.65rem 0.8rem;
                    border-radius: 8px;
                    background: #f7fafc;
                    border: 1px solid #e2e8f0;
                    text-align: center;
                }
                .ar-kpi-item.pkr { background: rgba(39,174,96,0.06); border-color: rgba(39,174,96,0.2); }
                .ar-kpi-item.usd { background: rgba(52,152,219,0.06); border-color: rgba(52,152,219,0.2); }
                .ar-kpi-item.comm-pkr { background: rgba(22,163,74,0.1); border-color: rgba(22,163,74,0.3); }
                .ar-kpi-item.comm-usd { background: rgba(37,99,235,0.1); border-color: rgba(37,99,235,0.3); }
                .ar-kpi-label { font-size: 7.5pt; text-transform: uppercase; letter-spacing: 0.05em; color: #718096; font-weight: 600; margin-bottom: 0.25rem; }
                .ar-kpi-value { font-size: 12pt; font-weight: 700; color: #1a202c; line-height: 1.2; }
                .ar-kpi-item.comm-pkr .ar-kpi-value { color: #15803d; }
                .ar-kpi-item.comm-usd .ar-kpi-value { color: #1d4ed8; }
                .ar-kpi-sub { font-size: 7.5pt; color: #a0aec0; margin-top: 2px; }

                /* Payable banner */
                .ar-payable-banner {
                    display: flex; align-items: center; gap: 0.75rem;
                    padding: 0.75rem 1rem;
                    border-radius: 8px;
                    background: rgba(22,163,74,0.08);
                    border-left: 4px solid #16a34a;
                    margin-bottom: 1.25rem;
                    font-size: 11pt;
                }
                .ar-payable-banner i { font-size: 1.3rem; color: #16a34a; flex-shrink: 0; }
                .ar-payable-banner div { display: flex; flex-wrap: wrap; align-items: center; gap: 0.5rem; }
                .ar-payable-banner strong { color: #1a202c; margin-right: 0.3rem; }
                .ar-payable-banner span { color: #15803d; font-weight: 700; font-size: 12pt; }

                /* Section heading */
                .ar-section-heading { font-size: 11pt; font-weight: 700; color: #2d3748; margin: 0 0 0.6rem; }

                /* Table */
                .ar-table-wrap { width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; margin-bottom: 1rem; }
                .ar-table {
                    width: 100%; border-collapse: separate; border-spacing: 0;
                    font-size: 9.5pt; border-bottom: 1px solid #9a9a9a;
                }
                .ar-table thead th {
                    background: #9a9a9a; color: #fff; padding: 7px 10px;
                    text-align: left; font-size: 8.5pt; font-weight: 700;
                    border-bottom: 2px solid #555; white-space: nowrap;
                }
                .ar-th-right { text-align: right !important; }
                .ar-table tbody tr:nth-child(even) td { background: #f5f5f5; }
                .ar-table tbody td { padding: 8px 10px; vertical-align: middle; border-bottom: 1px solid #e2e8f0; }
                .ar-td-right { text-align: right !important; white-space: nowrap; }
                .ar-td-center { text-align: center !important; }
                .ar-td-label { text-align: right !important; padding-right: 12px !important; font-weight: 700; color: #2d3748; }
                .ar-comm { color: #15803d !important; font-weight: 600; }
                .ar-total-row td {
                    background: #f0fdf4 !important; border-top: 2px solid #555;
                    font-weight: 700; padding: 9px 10px;
                }
                .ar-table tfoot .ar-comm { color: #15803d !important; }

                /* Notes */
                .ar-notes { font-size: 8.5pt; color: #718096; padding-top: 0.75rem; border-top: 1px solid #e2e8f0; }
                .ar-notes p { margin: 0 0 4px; line-height: 1.5; }

                /* Print */
                @media print {
                    body * { visibility: hidden; }
                    .ar-report-wrap, .ar-report-wrap * { visibility: visible; }
                    .ar-report-wrap {
                        position: fixed; top: 0; left: 0;
                        width: 100%; padding: 0.4in;
                        box-sizing: border-box; background: #fff;
                    }
                    .ar-toolbar, .no-print { display: none !important; }
                    .ar-table thead th,
                    .ar-table tbody td,
                    .ar-table tfoot td {
                        -webkit-print-color-adjust: exact; print-color-adjust: exact;
                    }
                    @page { size: A4 landscape; margin: 0; }
                }

                /* Responsive KPI grid */
                @media (max-width: 900px) {
                    .ar-kpi-grid { grid-template-columns: repeat(3, 1fr); }
                }
                @media (max-width: 600px) {
                    .ar-kpi-grid { grid-template-columns: repeat(2, 1fr); }
                    .ar-report-wrap { padding: 1rem; }
                    .ar-agent-details { flex-direction: column; gap: 0.4rem; }
                }
            `}</style>

            <Toast
                show={toast.show}
                message={toast.message}
                type={toast.type}
                onClose={() => setToast({ show: false, message: '', type: 'success' })}
            />
        </div>
    );
};

export default SalesAgentManagement;
